import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Button } from "../../../../components/ui/Button";
import { useDispatch, useSelector } from "react-redux";
import { resetWizard, setSelectedFixture, setStep } from "../../../../redux/Slices/matchSchedulerSlice";
import { useCreateMatchSchedule } from "../../../../queries/matchScheduler";
import MatchSchedulerForm, { buildMatchSchedulePayload } from "./MatchSchedulerForm";
import FixtureBrowser from "./FixtureBrowser";

/**
 * One dialog, two entry points.
 *
 *   <MatchSchedulerDialog open fixture={fx} onClose={…} />   "Schedule This Match"
 *   <MatchSchedulerDialog open onClose={…} />                 "+ Create"
 *
 * Replaces the old 363-line wizard (which was rendered nowhere — dead code) and
 * QuickScheduleDialog (246 lines, the only live path). They differed mainly in
 * state ownership, Redux vs local props, which is the duplication a shared form
 * alone would have moved rather than removed. Chrome lives here; fields live in
 * MatchSchedulerForm.
 */
export default function MatchSchedulerDialog({ open, fixture: fixtureProp = null, onClose }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { selectedFixture, step } = useSelector((s) => s.matchScheduler);
    const createMutation = useCreateMatchSchedule();
    const [cfg, setCfg] = useState({ durationMin: 105, screenIds: [], notes: "" });

    // The prop wins: it means the caller already picked a fixture.
    const fixture = fixtureProp ?? selectedFixture;
    const quickMode = !!fixtureProp;

    useEffect(() => {
        if (!open) return;
        setCfg({ durationMin: 105, screenIds: [], notes: "" });
        dispatch(setStep(quickMode ? 2 : 1));
        if (!quickMode) dispatch(setSelectedFixture(null));
    }, [open, quickMode, fixtureProp?.fixture_id, dispatch]);

    const close = () => {
        dispatch(resetWizard());
        onClose?.();
    };

    const submit = () => {
        const payload = buildMatchSchedulePayload(fixture, cfg);
        if (!payload) return;
        createMutation.mutate(payload, { onSuccess: close });
    };

    const footer = (
        <div className="flex justify-between w-full">
            <Button
                label={t('buttons.back')} icon="pi pi-arrow-left" text
                disabled={quickMode || step === 1}
                onClick={() => dispatch(setStep(1))}
            />
            <div className="flex gap-2">
                <Button label={t('buttons.cancel')} text onClick={close} />
                {step === 1 && !quickMode ? (
                    <Button
                        label={t('buttons.next')} icon="pi pi-arrow-right" iconPos="right"
                        disabled={!fixture} onClick={() => dispatch(setStep(2))}
                    />
                ) : (
                    <Button
                        label={t('match_scheduler.schedule_match')} icon="pi pi-check"
                        loading={createMutation.isLoading} disabled={!fixture} onClick={submit}
                    />
                )}
            </div>
        </div>
    );

    return (
        <Dialog
            visible={open}
            onHide={close}
            header={quickMode ? t('match_scheduler.schedule_match') : t('match_scheduler.new_schedule')}
            footer={footer}
            style={{ width: "min(46rem, 95vw)" }}
            breakpoints={{ "960px": "95vw" }}
            dismissableMask
        >
            {step === 1 && !quickMode
                ? <FixtureBrowser />
                : <MatchSchedulerForm fixture={fixture} value={cfg} onChange={setCfg} />}
        </Dialog>
    );
}
